/**
 * Accounts API client — the fetch equivalent of the Nuxt console's
 * DeveloperSettingsService and AccountService.
 *
 * Two differences from the original worth knowing about: the bearer token is read per
 * request rather than captured once at module load (the old client froze whatever token
 * existed when the bundle evaluated, so a refreshed token was ignored until reload), and a
 * 401 triggers a single token refresh and retry before giving up.
 */

import { clearToken, redirectToLogin, refreshToken, storedToken } from './auth.ts';
import { consoleConfig } from './config.ts';

export interface DeveloperApp {
  id: string;
  name: string;
  clientID: string;
  clientSecret?: string;
  avatar?: string;
  redirectUris?: string[];
  createdAt?: string;
}

/** Response of POST /images/upload_url on streams-api. */
export interface ImageUploadTarget {
  uploadId: string;
  uploadUrl: string;
}

export interface Account {
  id?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string;
}

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Which service to talk to. Accounts unless stated otherwise. */
  service?: 'accounts' | 'streams';
  /** Set internally to stop a refreshed request from looping. */
  retry?: boolean;
}

/** Longest error body worth showing verbatim. Anything larger is not a message. */
const MAX_ERROR_CHARS = 300;

/**
 * Turns a failed response into one short sentence.
 *
 * Never returns a raw body wholesale: when a request misses the API — a proxy answers, a
 * base URL is wrong, the dev server serves its own 404 page — the body is a full HTML
 * document, and dumping that into the UI is how a config mistake ends up looking like a
 * page of markup pasted under a form field.
 */
async function readError(response: Response): Promise<string> {
  const fallback = `${response.statusText || 'Request failed'} (${response.status})`;

  try {
    const text = (await response.text()).trim();
    if (!text) return fallback;
    if (/^<(!doctype|html|\?xml)/i.test(text)) return fallback;

    try {
      const parsed = JSON.parse(text) as { message?: string | string[]; error?: string };
      const message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message;
      const picked = message || parsed.error;
      return picked ? String(picked).slice(0, MAX_ERROR_CHARS) : fallback;
    } catch {
      return text.length > MAX_ERROR_CHARS ? fallback : text;
    }
  } catch {
    return fallback;
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const config = consoleConfig();
  const service = options.service ?? 'accounts';
  const base = service === 'streams' ? config.streamsApiBase : config.accountsApiBase;
  const token = storedToken();

  // Without this an absent base silently becomes a relative URL, and the request lands on
  // whatever is serving the page instead of the API. Fail where the mistake actually is.
  if (!base) {
    throw new ApiError(
      0,
      `No ${service} API base is configured. The page was built before this setting ` +
        'existed — restart the dev server, or rebuild the site.',
    );
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  // Deliberately NOT `credentials: 'include'`: the bearer token is the only thing that
  // authenticates here, and asking for cookies would force credentialed CORS on every
  // accounts response. Only the token exchange in auth.ts needs the session cookie.
  const response = await fetch(`${base}${path}`, {
    method: options.method ?? 'GET',
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });

  if (response.status === 401 && !options.retry) {
    clearToken();
    const fresh = await refreshToken();
    if (fresh) return request<T>(path, { ...options, retry: true });
    redirectToLogin();
    throw new ApiError(401, 'Your session has expired. Redirecting you to sign in…');
  }

  if (!response.ok) throw new ApiError(response.status, await readError(response));

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const getAccount = () => request<Account>('/account/this');

export const getApplications = () => request<DeveloperApp[]>('/developer/apps');

export const getApplication = (appId: string) =>
  request<DeveloperApp>(`/developer/apps/${encodeURIComponent(appId)}`);

export const createApplication = (data: { name: string; avatar?: string }) =>
  request<DeveloperApp>('/developer/apps', { method: 'POST', body: data });

/** Replaces the mutable fields. Responds `{ success: true }`, not the updated app. */
export const updateApplication = (
  appId: string,
  data: { name: string; avatar?: string; redirectUris: string[] },
) => request<{ success?: boolean }>(`/developer/apps/${encodeURIComponent(appId)}`, {
  method: 'PUT',
  body: data,
});

export const deleteApplication = (appId: string) =>
  request<void>(`/developer/apps/${encodeURIComponent(appId)}`, { method: 'DELETE' });

/* ------------------------------------------------------------------ image service */
/*
 * streams-api's shared image endpoints — the same ones streams-app uses for destination
 * poster images and player overlays. Nothing here is app-specific, so avatars reuse them
 * rather than needing a new endpoint on the accounts API.
 */

export const createImageUploadUrl = (body: {
  assetType: string;
  type: string;
  size: number;
  originalFilename: string;
}) => request<ImageUploadTarget>('/images/upload_url', { method: 'POST', body, service: 'streams' });

/** Commits the record once the bytes have landed. */
export const markImageUploaded = (imageId: string) =>
  request<void>(`/images/${encodeURIComponent(imageId)}`, { method: 'PUT', service: 'streams' });

export const deleteImage = (imageId: string) =>
  request<void>(`/images/${encodeURIComponent(imageId)}`, { method: 'DELETE', service: 'streams' });

export const suggestApplication = () =>
  request<{ name?: string; avatar?: string }>('/developer/apps/suggestions');
